<template>
  <div class="file-edit-container">
    <el-form 
      :model="form" 
      :rules="rules" 
      ref="editForm" 
      label-width="100px"
      class="edit-form"
    >
      <!-- 第一行：文件名称 + 文件类型 -->
      <div class="form-row">
        <el-form-item label="文件名称" prop="name" class="form-item-half">
          <el-input 
            v-model="form.name" 
            placeholder="请输入文件名称"
            clearable
            maxlength="100"
            show-word-limit
          />
        </el-form-item>
        <el-form-item label="文件类型" prop="type" class="form-item-half">
          <el-select 
            v-model="form.type" 
            placeholder="请选择文件类型"
            style="width: 100%"
          >
            <el-option label="PPT" :value="1" />
            <el-option label="Word" :value="2" />
          </el-select>
        </el-form-item>
      </div>

      <!-- 第二行：作者 + 页数 -->
      <div class="form-row">
        <el-form-item label="作者" prop="author" class="form-item-half">
          <el-input 
            v-model="form.author" 
            placeholder="请输入作者名称"
            clearable
            maxlength="50"
          />
        </el-form-item>
        <el-form-item label="页数" prop="page" class="form-item-half">
          <el-input-number 
            v-model="form.page" 
            :min="1" 
            :max="9999"
            placeholder="页数"
            style="width: 100%"
          />
        </el-form-item>
      </div>

      <!-- 第三行：价格 + 状态 -->
      <div class="form-row">
        <el-form-item label="价格" prop="price" class="form-item-half">
          <el-input-number 
            v-model="form.price" 
            :min="0" 
            :max="9999.99"
            :precision="2"
            placeholder="价格"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item label="状态" prop="state" class="form-item-half">
          <el-select 
            v-model="form.state" 
            placeholder="请选择状态"
            style="width: 100%"
          >
            <el-option label="正常" :value="1" />
            <el-option label="下架" :value="0" />
          </el-select>
        </el-form-item>
      </div>

      <!-- 第四行：文件格式 + 分类（仅新文科模块） -->
      <div class="form-row" v-if="showCategoryFields">
        <el-form-item label="文件格式" prop="format" class="form-item-half">
          <el-select 
            v-model="form.format" 
            placeholder="请选择文件格式"
            style="width: 100%"
          >
            <el-option
              v-for="format in fileFormats"
              :key="format.format"
              :label="format.name"
              :value="format.format"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="大分类" prop="categoryId" class="form-item-half">
          <el-select 
            v-model="form.categoryId" 
            placeholder="请选择大分类"
            style="width: 100%"
            @change="onCategoryChange"
          >
            <el-option
              v-for="category in categories"
              :key="category.id"
              :label="category.name"
              :value="category.id"
            />
          </el-select>
        </el-form-item>
      </div>

      <!-- 第四行：文件格式（仅旧模块） -->
      <div class="form-row" v-if="!showCategoryFields">
        <el-form-item label="文件格式" prop="format" class="form-item-half">
          <el-select 
            v-model="form.format" 
            placeholder="请选择文件格式"
            style="width: 100%"
          >
            <el-option
              v-for="format in fileFormats"
              :key="format.format"
              :label="format.name"
              :value="format.format"
            />
          </el-select>
        </el-form-item>
        <el-form-item class="form-item-half">
          <!-- 占位 -->
        </el-form-item>
      </div>

      <!-- 第五行：子分类（仅新文科模块） -->
      <div class="form-row" v-if="showCategoryFields">
        <el-form-item label="子分类" prop="subcategoryId" class="form-item-half">
          <el-select 
            v-model="form.subcategoryId" 
            :placeholder="form.categoryId ? '请选择子分类' : '请先选择大分类'"
            style="width: 100%"
            :disabled="!form.categoryId"
          >
            <el-option
              v-for="subcategory in subcategories"
              :key="subcategory.id"
              :label="subcategory.name"
              :value="subcategory.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item class="form-item-half">
          <!-- 占位 -->
        </el-form-item>
      </div>

      <!-- 第六行：描述 -->
      <div class="form-row">
        <el-form-item label="描述" prop="description" class="form-item-full">
          <el-input 
            v-model="form.description" 
            type="textarea" 
            :rows="3"
            placeholder="请输入文件描述"
            maxlength="500"
            show-word-limit
          />
        </el-form-item>
      </div>

      <!-- 文件信息显示区域 -->
      <div class="file-info-section">
        <h4>当前文件信息</h4>
        <div class="file-info-grid">
          <div class="file-info-item">
            <span class="label">文件：</span>
            <span class="value">{{ originalFile.name || '无' }}</span>
          </div>
          <div class="file-info-item">
            <span class="label">封面：</span>
            <span class="value">{{ originalFile.cover ? '已设置' : '无' }}</span>
          </div>
          <div class="file-info-item">
            <span class="label">预览图：</span>
            <span class="value">{{ originalFile.images ? originalFile.images.split(',').length + '张' : '无' }}</span>
          </div>
        </div>
      </div>

      <!-- 文件替换区域 -->
      <div class="file-replace-section">
        <h4>文件替换（可选）</h4>
        <p class="replace-tip">如需替换文件，请上传新文件。不选择文件则保持原文件不变。</p>
        
        <!-- 文件上传 -->
        <el-form-item label="文件" class="file-upload-item">
          <el-upload
            :key="`file-upload-${fileData?.id || 'new'}`"
            ref="fileUpload"
            :action="uploadUrl"
            :headers="uploadHeaders"
            :data="uploadData"
            :file-list="fileList"
            :before-upload="beforeFileUpload"
            :on-success="onFileUploadSuccess"
            :on-error="onFileUploadError"
            :on-remove="onFileRemove"
            :accept="acceptedFileTypes"
            :limit="1"
            :auto-upload="true"
            drag
          >
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
            <div class="el-upload__tip" slot="tip">只能上传PPT、Word文件，且不超过100MB</div>
          </el-upload>
        </el-form-item>

        <!-- 封面图片上传 -->
        <el-form-item label="封面图片" class="file-upload-item">
          <el-upload
            :key="`cover-upload-${fileData?.id || 'new'}`"
            ref="coverUpload"
            :action="uploadUrl"
            :headers="uploadHeaders"
            :data="coverUploadData"
            :file-list="coverFileList"
            :before-upload="beforeCoverUpload"
            :on-success="onCoverUploadSuccess"
            :on-error="onCoverUploadError"
            :on-remove="onCoverRemove"
            :accept="acceptedImageTypes"
            :limit="1"
            :auto-upload="true"
            list-type="picture-card"
          >
            <i class="el-icon-plus"></i>
          </el-upload>
          <div class="upload-tip">支持JPG、PNG格式，建议尺寸16:9</div>
        </el-form-item>

        <!-- 预览图片上传 -->
        <el-form-item label="预览图片" class="file-upload-item">
          <el-upload
            :key="`image-upload-${fileData?.id || 'new'}`"
            ref="imageUpload"
            :action="uploadUrl"
            :headers="uploadHeaders"
            :data="imageUploadData"
            :file-list="imageFileList"
            :before-upload="beforeImageUpload"
            :on-success="onImageUploadSuccess"
            :on-error="onImageUploadError"
            :on-remove="onImageRemove"
            :accept="acceptedImageTypes"
            :limit="3"
            :auto-upload="true"
            list-type="picture-card"
          >
            <i class="el-icon-plus"></i>
          </el-upload>
          <div class="upload-tip">最多3张，支持JPG、PNG格式</div>
        </el-form-item>
      </div>

      <!-- 操作按钮 -->
      <div class="form-actions">
        <el-button @click="handleCancel">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitting">
          {{ submitting ? '保存中...' : '保存修改' }}
        </el-button>
      </div>
    </el-form>
  </div>
</template>

<script>
import api from '@/api/api';

export default {
  name: 'FileEdit',
  props: {
    // 当前模块类型
    module: {
      type: String,
      default: 'all'
    },
    // 要编辑的文件数据
    fileData: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      submitting: false,
      form: {
        name: '',
        type: 1,
        author: '',
        page: 1,
        price: 0,
        state: 1,
        format: '',
        categoryId: null,
        subcategoryId: null,
        description: '',
        fileUrl: '',
        cover: '',
        images: []
      },
      originalFile: {},
      rules: {
        name: [
          { required: true, message: '请输入文件名称', trigger: 'blur' },
          { min: 2, max: 100, message: '文件名称长度在 2 到 100 个字符', trigger: 'blur' }
        ],
        type: [
          { required: true, message: '请选择文件类型', trigger: 'change' }
        ],
        author: [
          { required: true, message: '请输入作者名称', trigger: 'blur' },
          { min: 2, max: 50, message: '作者名称长度在 2 到 50 个字符', trigger: 'blur' }
        ],
        page: [
          { required: true, message: '请输入页数', trigger: 'blur' },
          { type: 'number', min: 1, max: 9999, message: '页数必须在 1 到 9999 之间', trigger: 'blur' }
        ],
        price: [
          { required: true, message: '请输入价格', trigger: 'blur' },
          { type: 'number', min: 0, max: 9999.99, message: '价格必须在 0 到 9999.99 之间', trigger: 'blur' }
        ],
        state: [
          { required: true, message: '请选择状态', trigger: 'change' }
        ],
         format: [
           { required: true, message: '请选择文件格式', trigger: 'change' }
         ],
         categoryId: [
           { required: true, message: '请选择大分类', trigger: 'change' }
         ],
         subcategoryId: [
           { required: true, message: '请选择子分类', trigger: 'change' }
         ]
      },
      fileList: [],
      coverFileList: [],
      imageFileList: [],
      categories: [],
      subcategories: [],
      allSubcategories: [],
      fileFormats: [],
      uploadUrl: process.env.VUE_APP_API_BASE_URL + '/api/upload/file',
      uploadHeaders: {
        'Authorization': 'Bearer ' + localStorage.getItem('admin_token')
      },
      uploadData: {},
      coverUploadData: {},
      imageUploadData: {},
      acceptedFileTypes: '.ppt,.pptx,.doc,.docx',
      acceptedImageTypes: '.jpg,.jpeg,.png'
    };
  },
  computed: {
    showCategoryFields() {
      return this.module === 'xinwenke';
    }
  },
  watch: {
    fileData: {
      handler(newVal) {
        if (newVal) {
          this.initFormData();
          // 强制重新渲染上传组件
          this.$nextTick(() => {
            this.forceUpdateUploadComponents();
          });
        }
      },
      immediate: true,
      deep: true
    }
  },
   async mounted() {
     await Promise.all([
       this.loadCategories(),
       this.loadFileFormats(),
       this.loadSubcategories()
     ]);
     
     // 数据加载完成后，如果有文件数据，重新初始化表单
     if (this.fileData) {
       this.initFormData();
     }
   },
  methods: {
     // 初始化表单数据
     initFormData() {
       if (this.fileData) {
         this.originalFile = { ...this.fileData };
         // 处理预览图数据
         let previewImages = [];
         if (this.fileData.images) {
           previewImages = this.fileData.images.split(',').filter(url => url.trim());
         } else if (this.fileData.image) {
           previewImages = this.fileData.image.split(',').filter(url => url.trim());
         }

         this.form = {
           name: this.fileData.name || '',
           type: this.fileData.type || 1,
           author: this.fileData.author || '',
           page: this.fileData.page || 1,
           price: this.fileData.price || 0,
           state: this.fileData.state !== undefined ? this.fileData.state : 1,
           format: this.fileData.format || '',
           categoryId: this.fileData.categoryId || null,
           subcategoryId: this.fileData.subcategoryId || null,
           description: this.fileData.description || this.fileData.describes || '',
           fileUrl: this.fileData.fileUrl || this.fileData.url || '',
           cover: this.fileData.cover || '',
           images: previewImages
         };
         
         // 如果有分类ID，需要加载对应的子分类
         if (this.form.categoryId && this.allSubcategories.length > 0) {
           this.subcategories = this.allSubcategories.filter(sub => sub.categoryId === this.form.categoryId);
         }
         
         // 初始化文件列表显示
         this.initFileLists();
       }
     },

    // 初始化文件列表
    initFileLists() {
      // 清空文件列表
      this.fileList = [];
      this.coverFileList = [];
      this.imageFileList = [];

      // 如果有原文件，显示原文件信息
      if (this.form.fileUrl) {
        this.fileList = [{
          name: this.originalFile.name || '原文件',
          url: this.form.fileUrl,
          status: 'success'
        }];
      }

      // 封面图片处理
      if (this.form.cover) {
        this.coverFileList = [{
          name: '封面图片',
          url: this.form.cover,
          status: 'success'
        }];
      }

      // 预览图片处理 - 支持多种数据格式
      let previewImages = [];
      
      // 处理 images 字段（数组格式）
      if (this.form.images && this.form.images.length > 0) {
        previewImages = this.form.images;
      }
      // 处理原始数据中的 image 字段（字符串格式，逗号分隔）
      else if (this.originalFile.image) {
        previewImages = this.originalFile.image.split(',').filter(url => url.trim());
      }
      // 处理原始数据中的 images 字段（字符串格式，逗号分隔）
      else if (this.originalFile.images) {
        previewImages = this.originalFile.images.split(',').filter(url => url.trim());
      }

      if (previewImages.length > 0) {
        this.imageFileList = previewImages.map((url, index) => ({
          name: `预览图${index + 1}`,
          url: url.trim(),
          status: 'success'
        }));
      }

      // 文件数据初始化完成
    },

     // 加载分类数据
     async loadCategories() {
       try {
         const response = await api.getCategories();
         if (response.data && response.data.code === 200) {
           this.categories = response.data.data || [];
           // 分类数据加载成功
         }
       } catch (error) {
         // 分类数据加载失败，静默处理
       }
     },

    // 加载文件格式数据
    async loadFileFormats() {
      try {
        const response = await api.getFileFormats();
        if (response.data && response.data.code === 200) {
          this.fileFormats = response.data.data || [];
        }
      } catch (error) {
        // 静默处理错误
      }
    },

     // 加载子分类数据
     async loadSubcategories() {
       try {
         const response = await api.getSubcategories();
         if (response.data && response.data.code === 200) {
           this.allSubcategories = response.data.data || [];
           // 子分类数据加载成功
         }
       } catch (error) {
         // 子分类数据加载失败，静默处理
       }
     },

     // 分类变化处理
     onCategoryChange(categoryId) {
       // 大分类变化处理
       this.form.subcategoryId = null;
       this.subcategories = this.allSubcategories.filter(sub => sub.categoryId === categoryId);
       // 过滤后的子分类已更新
       
       // 如果选择了大分类，清空子分类选择并触发验证
       if (categoryId) {
         this.$nextTick(() => {
           this.$refs.editForm.validateField('subcategoryId');
         });
       }
     },

    // 文件上传前处理
    beforeFileUpload(file) {
      const isValidType = this.acceptedFileTypes.split(',').some(type => 
        file.name.toLowerCase().endsWith(type.replace('.', ''))
      );
      if (!isValidType) {
        this.$message.error('只能上传PPT、Word文件！');
        return false;
      }
      const isValidSize = file.size / 1024 / 1024 < 100;
      if (!isValidSize) {
        this.$message.error('文件大小不能超过100MB！');
        return false;
      }
      return true;
    },

    // 文件上传成功
    onFileUploadSuccess(response, file) {
      if (response && response.code === 200) {
        this.form.fileUrl = response.data.url;
        this.$message.success('文件上传成功！');
      } else {
        this.$message.error(response.msg || '文件上传失败！');
      }
    },

    // 文件上传失败
    onFileUploadError(error) {
      this.$message.error('文件上传失败！');
    },

    // 文件移除
    onFileRemove() {
      this.form.fileUrl = '';
    },

    // 封面图片上传前处理
    beforeCoverUpload(file) {
      const isValidType = this.acceptedImageTypes.split(',').some(type => 
        file.name.toLowerCase().endsWith(type.replace('.', ''))
      );
      if (!isValidType) {
        this.$message.error('只能上传JPG、PNG图片！');
        return false;
      }
      const isValidSize = file.size / 1024 / 1024 < 5;
      if (!isValidSize) {
        this.$message.error('图片大小不能超过5MB！');
        return false;
      }
      return true;
    },

    // 封面图片上传成功
    onCoverUploadSuccess(response, file) {
      if (response && response.code === 200) {
        this.form.cover = response.data.url;
        this.$message.success('封面图片上传成功！');
      } else {
        this.$message.error(response.msg || '封面图片上传失败！');
      }
    },

    // 封面图片上传失败
    onCoverUploadError(error) {
      this.$message.error('封面图片上传失败！');
    },

    // 封面图片移除
    onCoverRemove() {
      this.form.cover = '';
    },

    // 预览图片上传前处理
    beforeImageUpload(file) {
      const isValidType = this.acceptedImageTypes.split(',').some(type => 
        file.name.toLowerCase().endsWith(type.replace('.', ''))
      );
      if (!isValidType) {
        this.$message.error('只能上传JPG、PNG图片！');
        return false;
      }
      const isValidSize = file.size / 1024 / 1024 < 5;
      if (!isValidSize) {
        this.$message.error('图片大小不能超过5MB！');
        return false;
      }
      return true;
    },

    // 预览图片上传成功
    onImageUploadSuccess(response, file) {
      if (response && response.code === 200) {
        if (!this.form.images) {
          this.form.images = [];
        }
        this.form.images.push(response.data.url);
        this.$message.success('预览图片上传成功！');
      } else {
        this.$message.error(response.msg || '预览图片上传失败！');
      }
    },

    // 预览图片上传失败
    onImageUploadError(error) {
      this.$message.error('预览图片上传失败！');
    },

    // 预览图片移除
    onImageRemove(file) {
      if (this.form.images) {
        const index = this.form.images.indexOf(file.url);
        if (index > -1) {
          this.form.images.splice(index, 1);
        }
      }
    },

     // 提交修改
     async handleSubmit() {
       try {
         // 新文科模块需要验证分类选择
         if (this.module === 'xinwenke') {
           if (!this.form.categoryId) {
             this.$message.error('请选择大分类');
             return;
           }
           if (!this.form.subcategoryId) {
             this.$message.error('请选择子分类');
             return;
           }
         }
         
         await this.$refs.editForm.validate();
         
         this.submitting = true;
        
        // 构建FormData，支持文件上传
        const formData = new FormData();
        
        // 基础信息
        formData.append('code', this.fileData.code); // 使用文件编码定位文件
        formData.append('name', this.form.name);
        formData.append('author', this.form.author);
        formData.append('pageCount', this.form.page);
        formData.append('price', this.form.price);
        formData.append('state', this.form.state);
        formData.append('type', this.form.type);
        formData.append('format', this.form.format || '');
        formData.append('describes', this.form.description || '');
        
        // 新文科模块特有字段
        if (this.module === 'xinwenke') {
          if (this.form.categoryId) {
            formData.append('categoryId', this.form.categoryId);
          }
          if (this.form.subcategoryId) {
            formData.append('subcategoryId', this.form.subcategoryId);
          }
        }
        
        // 文件替换（可选）- 检查是否有新上传的文件
        const fileInput = this.$refs.fileUpload;
        if (fileInput && fileInput.uploadFiles && fileInput.uploadFiles.length > 0) {
          const file = fileInput.uploadFiles[0].raw;
          if (file) {
            formData.append('file', file);
          }
        }
        
        // 封面图片替换（可选）- 检查是否有新上传的封面
        const coverInput = this.$refs.coverUpload;
        if (coverInput && coverInput.uploadFiles && coverInput.uploadFiles.length > 0) {
          const coverFile = coverInput.uploadFiles[0].raw;
          if (coverFile) {
            formData.append('coverFile', coverFile);
          }
        }
        
        // 预览图片替换（可选）- 检查是否有新上传的预览图
        const imageInput = this.$refs.imageUpload;
        if (imageInput && imageInput.uploadFiles && imageInput.uploadFiles.length > 0) {
          // 多张预览图
          imageInput.uploadFiles.forEach((fileItem, index) => {
            if (fileItem.raw) {
              formData.append('previewFiles', fileItem.raw);
            }
          });
        }
        
        // 根据模块调用不同的API
        let response;
        switch (this.module) {
          case 'xinwenke':
            response = await api.updateXinWenKeFile(formData);
            break;
          case 'internet':
            response = await api.updateSourceIndexFile(formData);
            break;
          case 'career':
            response = await api.updateSourceZyhgFile(formData);
            break;
          default:
            response = await api.updateXinWenKeFile(formData);
        }
        
        if (response.data && response.data.code === 200) {
          this.$message.success('文件修改成功！');
          this.$emit('edit-success');
          this.handleCancel();
        } else {
          this.$message.error(response.data?.msg || '文件修改失败！');
        }
      } catch (error) {
        this.$message.error('提交失败！');
      } finally {
        this.submitting = false;
      }
    },

    // 取消编辑
    handleCancel() {
      this.$emit('edit-cancel');
    },

    // 强制更新上传组件
    forceUpdateUploadComponents() {
      // 使用更简单的方法强制更新组件
      this.$forceUpdate();
    }
  }
};
</script>

<style scoped>
.file-edit-container {
  padding: 15px;
  background: #fff;
  border-radius: 8px;
}

.edit-form {
  max-width: 100%;
}

.form-row {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
}

.form-item-half {
  flex: 1;
}

.form-item-full {
  width: 100%;
}

.file-info-section {
  margin: 30px 0;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.file-info-section h4 {
  margin: 0 0 15px 0;
  color: #333;
  font-size: 16px;
  font-weight: 600;
}

.file-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
}

.file-info-item {
  display: flex;
  align-items: center;
}

.file-info-item .label {
  font-weight: 500;
  color: #666;
  margin-right: 8px;
  min-width: 60px;
}

.file-info-item .value {
  color: #333;
  flex: 1;
}

.file-replace-section {
  margin: 30px 0;
  padding: 20px;
  background: #fff7e6;
  border-radius: 8px;
  border: 1px solid #ffd591;
}

.file-replace-section h4 {
  margin: 0 0 10px 0;
  color: #d46b08;
  font-size: 16px;
  font-weight: 600;
}

.replace-tip {
  margin: 0 0 20px 0;
  color: #d46b08;
  font-size: 14px;
  line-height: 1.5;
}

.file-upload-item {
  margin-bottom: 20px;
}

.upload-tip {
  margin-top: 8px;
  font-size: 12px;
  color: #999;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .form-row {
    flex-direction: column;
    gap: 0;
  }
  
  .form-item-half {
    margin-bottom: 20px;
  }
  
  .file-info-grid {
    grid-template-columns: 1fr;
  }
  
  .form-actions {
    flex-direction: column;
  }
  
  .form-actions .el-button {
    width: 100%;
  }
}
</style>
